import type { PeekReason } from "./renderer";

/** Why the peek went away. `retract` is the auto-retract timer (the user did
 * nothing), `close` is the X, `added`/`all_picks` are the two productive exits,
 * `gate` is the sportsbook disappearing under it. Keeping them apart is the whole
 * point: an untouched retract and a deliberate close mean opposite things about
 * the card. (No `teardown` value: `destroy()` deliberately never calls
 * `hidePeek()` — doing so would rebuild a chip into the shadow root being wiped —
 * so nothing could ever produce it.) */
export type PeekCloseReason =
  | "all_picks" | "retract" | "timeout" | "close" | "added" | "gate"
  // The 50-swipe stop (2026-09-09): the deck closed itself and the widget went
  // with it for the session. Distinct from "close" — nobody pressed anything.
  | "exhausted";

export interface InteractionCtx {
  device: string;
  locale: string;
}

export interface ExposureCtx {
  exposure_id: string;
}

export function peekOpenMeta(
  reason: PeekReason,
  index: number,
  ctx: InteractionCtx,
  exposure?: ExposureCtx,
): Record<string, unknown> {
  return { reason, pick_index: index, ...ctx,
    ...(exposure ? { dims: { exposure_id: exposure.exposure_id } } : {}) };
}

export function peekCloseMeta(
  reason: PeekCloseReason,
  dwellMs: number,
  picksSeen: number,
  ctx: InteractionCtx,
  exposure?: ExposureCtx,
): Record<string, unknown> {
  // A backwards clock (device sleep, NTP correction) can hand us a negative
  // delta. Clamp rather than send it: this number is averaged and shown to an
  // operator, and one negative outlier moves that average more than the real
  // sessions do.
  const dwell = Math.max(0, Math.round(dwellMs));
  return { reason, dwell_ms: dwell, picks_seen: Math.max(0, picksSeen), ...ctx,
    ...(exposure ? { dims: { exposure_id: exposure.exposure_id } } : {}) };
}

/**
 * The full sheet closed, with the VISIBLE ms it was up.
 *
 * Same clamp as `peekCloseMeta`, for the same reason: this figure is summed and
 * averaged on an operator dashboard, where one negative outlier from a backwards
 * clock moves the number more than the real sessions do.
 */
export function panelCloseMeta(
  engagedMs: number,
  ctx: InteractionCtx,
): Record<string, unknown> {
  return { engaged_ms: Math.max(0, Math.round(engagedMs)), ...ctx };
}

export function peekNavMeta(
  method: "swipe" | "button",
  direction: "next" | "prev",
  toIndex: number,
  fromIndex: number,
  ctx: InteractionCtx,
  exposure?: ExposureCtx,
): Record<string, unknown> {
  return { method, direction, to_index: toIndex, from_index: fromIndex, ...ctx,
    ...(exposure ? { dims: { exposure_id: exposure.exposure_id } } : {}) };
}
